import React, { useEffect, useState, useMemo } from "react";
import { getDataWithToken } from "../api/api";
import { GET_SESSION_HISTORY, IMAGE_API_URL, GETGAMES } from "../helpers/Constant";
import { useTranslation } from "react-i18next";
import { BsCheck } from "react-icons/bs";
import { FaCheckCircle } from "react-icons/fa";
import { getUserInfo } from "../utils/common";


const WEEK_ONE_ROW = [
    { type: 'day', n: 1 },
    { type: 'day', n: 2 },
    { type: 'day', n: 3 },
    { type: 'day', n: 4 },
    { type: 'day', n: 5 },
    { type: 'day', n: 6 },
    { type: 'muver', milestone: 7 },
];

function SessionHistory({ showSessionHistoryModal, sessionId, closeSessionHistoryModal, dailyBurned, dailyTarget, dailyTargetPercentage, showDailyTarget = true, showRetentionData = false }) {
    const [sessionHistory, setSessionHistory] = useState([]);
    const [gamesData, setGamesData] = useState([]);
    const [loading, setLoading] = useState(true);
    const userData = getUserInfo();
    
    const { t } = useTranslation();

    const streakDay = useMemo(() => {
        const n = Number(userData?.userStreaks?.dailyStreak);
        if (!Number.isFinite(n) || n < 1) return 0;
        return Math.floor(n);
    }, [userData?.userStreaks?.dailyStreak]);

    const isInRetentionFlow = !userData?.userAccount?.isRetentionFlowCompleted && !userData?.userAccount?.isPrimeUser;
    const displayStreakDay = isInRetentionFlow ? Math.min(streakDay, 7) : streakDay;

    async function fetchData() {
        if(!sessionId) {
            setLoading(false);
            return false;
        }
        const response = await getDataWithToken(`${GET_SESSION_HISTORY}${sessionId}`);

        if (response && response.length > 0) {
            const gamesResponse = await getDataWithToken(GETGAMES);
            if (gamesResponse && gamesResponse.length > 0) {
                setGamesData(gamesResponse);
            }
            setSessionHistory(response);
        }
        setLoading(false);
    }

    useEffect(() => {
        fetchData();
    }, [sessionId]);

    const getGameImage = (gameName) => {
        if(!gamesData) return '';
        const game = gamesData.find(game => game.title === gameName);
        return game ? `${IMAGE_API_URL}${game.image}` : '';
    }

    const weekRows = useMemo(() => {
        if (isInRetentionFlow) {
            return [WEEK_ONE_ROW.map((slot) => (
                slot.type === 'day'
                    ? { ...slot }
                    : { ...slot, milestone: 7 }
            ))];
        }
        const normalizedStreak = Math.max(1, streakDay || 1);
        const currentWeekIndex = Math.floor((normalizedStreak - 1) / 7);
        const startDay = currentWeekIndex * 7 + 1;
        return [
            WEEK_ONE_ROW.map((slot) => (
                slot.type === 'day'
                    ? { ...slot, n: startDay + (slot.n - 1) }
                    : { ...slot, milestone: startDay + 6 }
            ))
        ];
    }, [streakDay, isInRetentionFlow]);

    function LoadingDotsIcon() {
        return (
            <svg xmlns='http://www.w3.org/2000/svg' width='16' height='4' viewBox='0 0 16 4' fill='currentColor' className='text-warning position-relative z-1'>
                <circle cx='2' cy='2' r='2' transform='translate(0.464 0.238)' />
                <circle cx='2' cy='2' r='2' transform='translate(6.464 0.238)' opacity='0.46' />
                <circle cx='2' cy='2' r='2' transform='translate(12.464 0.238)' opacity='0.15' />
            </svg>
        );
    }

    const ringValue = (dayNum) => {
        if (dayNum < displayStreakDay) return 100;
        return 0;
    };

    const currentWeekProgress = isInRetentionFlow
        ? displayStreakDay
        : ((streakDay - 1) % 7) + 1;
    const daysLeftToWeekReward = isInRetentionFlow
        ? Math.max(0, 8 - displayStreakDay)
        : Math.max(0, 8 - currentWeekProgress);
    const renderDayCell = (dayNum, calendar) => {
        const completed = dayNum < displayStreakDay;
        const active = dayNum === displayStreakDay;

        return (
            <div key={dayNum} className={dayWrapperClass(dayNum, calendar)}>
                <div className='day_name fw-light mb-2'>Day {dayNum}</div>
                <div
                    className='date d-flex align-items-center justify-content-center rounded-circle position-relative'
                    role='progressbar'
                    aria-valuemin='0'
                    aria-valuemax='100'
                    style={{ '--value': `${ringValue(dayNum)}` }}
                >
                    {completed && <FaCheckCircle className='text-warning position-relative z-1' />}
                    {active && <LoadingDotsIcon />}
                </div>
            </div>
        );
    };

    const dayWrapperClass = (dayNum, calendar) => {
        if (dayNum < displayStreakDay) return 'day reward_collected text-center day_completed';
        if (dayNum === displayStreakDay) {
            return calendar ? 'day reward_collected text-center day_in_progress' : 'day reward_collected text-center day_active';
        }
        return 'day reward_collected text-center';
    };

    const muverWrapperClass = (milestone, calendar) => {
        const done = isInRetentionFlow
            ? displayStreakDay > milestone
            : (milestone === 31 ? streakDay > 30 : streakDay > milestone);
        const active = isInRetentionFlow
            ? displayStreakDay === milestone
            : (milestone === 31 ? streakDay === 31 : streakDay === milestone);
        if (done) return 'day reward_collected text-center day_completed';

        if (active) {
            return calendar
                ? 'day reward_collected text-center day_in_progress'
                : 'day reward_collected text-center day_active';
        }
        return 'day reward_collected text-center';
    };

    const renderMuverCell = (milestone, calendar) => {
        const treatAsDone = isInRetentionFlow
            ? displayStreakDay > milestone
            : (milestone === 31 ? streakDay > 30 : streakDay > milestone);
        const treatAsActive = isInRetentionFlow
            ? displayStreakDay === milestone
            : (milestone === 31 ? streakDay === 31 : streakDay === milestone);
        return (
            <div key={`muver-${milestone}`} className={muverWrapperClass(milestone, calendar)}>
                <div className='day_name fw-light mb-2'>Muver</div>
                <div
                    className='date d-flex align-items-center justify-content-center rounded-circle position-relative'
                    role='progressbar'
                    aria-valuemin='0'
                    aria-valuemax='100'
                    style={{ '--value': `${treatAsDone ? 100 : 0}` }}
                >
                    {treatAsDone && <FaCheckCircle className='text-warning position-relative z-1' />}
                    {treatAsActive && !treatAsDone && <LoadingDotsIcon />}
                </div>
            </div>
        );
    };

    return (
        <>
            <div className={`modal ${showSessionHistoryModal ? 'show' : ''}`} style={{ display: showSessionHistoryModal ? 'block' : 'none' }}>
                <div className="modal-dialog modal-dialog-centered" role="document">
                    <div className="modal-content modal_content info_modal bg-secondary p-3 pb-4">
                        <div className="modal-body p-0">
                            <h4 className="fw-bold mb-3 fs-3 text-warning text-center">You Are Getting Better</h4>
                            {showRetentionData && isInRetentionFlow && (
                                <>
                                    {weekRows.map((weekRow, weekIndex) => {
                                        const weekStart = weekIndex * 7 + 1;
                                        return (
                                            <div
                                                key={`week-${weekStart}`}
                                                className={`user_retention_weekly my-4`}
                                            >
                                                <div className='daily_reward d-flex justify-content-around align-items-center flex-fill'>
                                                    {weekRow.map((slot) =>
                                                        slot.type === 'day'
                                                            ? renderDayCell(slot.n, true)
                                                            : renderMuverCell(slot.milestone, true)
                                                    )}
                                                </div>
                                                <div className='giftbox_reward position-relative pb-2 overflow-hidden'>
                                                    <div className='giftbox_round_wrapper position-absolute top-50'>
                                                        <div className='giftbox_round giftbox_round_1 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                                                        <div className='giftbox_round giftbox_round_2 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                                                        <div className='giftbox_round giftbox_round_3 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                                                        <div className='giftbox_round giftbox_round_4 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                                                        <div className='giftbox_round giftbox_round_5 rounded-pill pe-none position-absolute top-50 start-50 translate-middle'></div>
                                                    </div>
                                                    <div className='position-relative d-flex align-items-start justify-content-between gap-1 mt-1'>
                                                        <div className={`giftbox_title fw-medium mx-auto ${daysLeftToWeekReward === 1 ? 'lh-1 ps-1' : ''}`}>
                                                            {daysLeftToWeekReward === 1 ? (
                                                                <>Reward unlocks after your first game session.</>
                                                            ) : (
                                                                <>
                                                                    {daysLeftToWeekReward} Day{daysLeftToWeekReward === 1 ? '' : 's'} Left
                                                                    <br />
                                                                    To Get A Reward
                                                                </>
                                                            )}
                                                        </div>
                                                        <img className='me-2' src='../images/giftbox.png' alt='Gift Box' />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </>
                            )}
                            {showDailyTarget && (
                                <div className='reward_wrapper position-relative mb-3'>
                                    <h6 className='text-white opacity-75 fw-bold mb-1'>{t('Daily Target')}</h6>
                                    <div className='d-flex align-items-center flex-wrap'>
                                        <div className='d-flex justify-content-between align-items-end my-2 w-100'>
                                            <div className='d-flex align-items-center '>
                                                <img src='../images/fireicon.svg' height={30} alt='fireicon'></img>
                                                <h2 className='ps-2 fw-bold text-light mb-0'><span className='text-warning text_shadow_green'>{dailyBurned}</span> / {(Math.round(dailyTarget)).toFixed(0)}</h2>
                                            </div>
                                            <h2 className='text-white fw-bold mb-0 opacity-75'>{(isNaN(dailyTargetPercentage) || dailyTargetPercentage === 0) ? 0 : dailyTargetPercentage}%</h2>
                                        </div>
                                        <div className='progressbar_track position-relative mb-4 bg-light w-100'>
                                            <div className='progressbar_thumb position-absolute start-0 top-0 bottom-0 bg-warning mw-100 rounded-1' style={{ width: dailyTargetPercentage + '%' }}></div>
                                            <div className={`reward_divider position-absolute d-flex align-items-center ${dailyTargetPercentage <= 25 ? 'reward_pending step_1' : 'reward_done_step_1'}`}>
                                                <span className='d-flex align-items-center justify-content-center rounded-circle point_icon'>{dailyTargetPercentage >= 25 ? < BsCheck color='black' /> : ''}</span>
                                                {dailyTargetPercentage <= 25 ? <span className='mb-0 text-white point_number'>50</span> : ''}
                                            </div>
                                            <div className={`reward_divider position-absolute d-flex align-items-center ${dailyTargetPercentage <= 50 ? 'reward_pending step_2' : 'reward_done_step_2'}`}>
                                                <span className='d-flex align-items-center justify-content-center rounded-circle point_icon'>{dailyTargetPercentage >= 50 ? < BsCheck color='black' size={20} /> : ''}</span>
                                                {dailyTargetPercentage <= 50 ? <span className='mb-0 text-white point_number'>50</span> : ''}
                                            </div>
                                            <div className={`reward_divider position-absolute d-flex align-items-center ${dailyTargetPercentage <= 75 ? 'reward_pending step_3' : 'reward_done_step_3'}`}>
                                                <span className='d-flex align-items-center justify-content-center rounded-circle point_icon'>{dailyTargetPercentage >= 75 ? < BsCheck color='black' size={20} /> : ''}</span>
                                                {dailyTargetPercentage <= 75 ? <span className='mb-0 text-white point_number'>50</span> : ''}
                                            </div>
                                            <div className={`reward_divider position-absolute d-flex align-items-center ${dailyTargetPercentage < 100 ? 'reward_pending step_4' : 'reward_done_step_4'}`}>
                                                <span className='d-flex align-items-center justify-content-center rounded-circle point_icon'>{dailyTargetPercentage >= 100 ? < BsCheck color='black' /> : ''}</span>
                                                {dailyTargetPercentage < 100 ? <span className='mb-0 text-white point_number'>50</span> : ''}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {sessionHistory && sessionHistory.length > 0 && (
                                <div className="session_wrapper text-light mb-2 pb-1">
                                    <div className="session_head d-flex justify-content-between text-center mb-3">
                                        <div className="session_head_col">
                                            <div className="session_head_label">No. of Games</div>
                                            <div className="session_head_text text-white fw-bold">{sessionHistory.length}</div>
                                        </div>
                                        <div className="border-start opacity-50"></div>
                                        <div className="session_head_col">
                                            <div className="session_head_label">Calorie Burn</div>
                                            <div className="session_head_text text-white fw-bold"><img src='../images/fireicon.svg' height={20} alt='fireicon' /> {isNaN(sessionHistory.reduce((acc, item) => acc + item.burnedCal, 0)) ? 0 : sessionHistory.reduce((acc, item) => acc + item.burnedCal, 0)}</div>
                                        </div>
                                        <div className="border-start opacity-50"></div>
                                        <div className="session_head_col">
                                            <div className="session_head_label">Avg Accuracy</div>
                                            <div className="session_head_text text-white fw-bold">{isNaN(sessionHistory.reduce((acc, item) => acc + item.accuracy, 0) / sessionHistory.length) ? 0 : (sessionHistory.reduce((acc, item) => acc + item.accuracy, 0) / sessionHistory.length).toFixed(2)}%</div>
                                        </div>
                                        <div className="border-start opacity-50"></div>
                                        <div className="session_head_col">
                                            <div className="session_head_label">Time</div>
                                            <div className="session_head_text text-white fw-bold">{isNaN(sessionHistory.reduce((acc, item) => acc + item.time, 0) / 60) ? 0 : Math.round(sessionHistory.reduce((acc, item) => acc + item.time, 0) / 60)} <span className="text-light">mins</span></div>
                                        </div>
                                    </div>
                                    <div className="session_content py-1">
                                        {sessionHistory.map((item) => (
                                        <div key={item._id} className="session_content_item d-flex justify-content-between">
                                            <div className="session_content_col d-flex gap-2">
                                                <div className={`session_content_image ${getGameImage(item.gameName) ? '' : 'bg-white'}`}>
                                                    {getGameImage(item.gameName) ? 
                                                        <img className="img-fluid" src={getGameImage(item.gameName)} alt={item.gameName} /> : 
                                                        <span className="d-flex align-items-center justify-content-center h-100 fw-bold">{item.gameName}</span>
                                                    }
                                                </div>
                                                <div className="ps-1">
                                                    <div className="session_content_label fw-medium mb-2">Calorie</div>
                                                    <div className="session_content_text text-white fw-bold"><img src='../images/fireicon.svg' height={20} alt='fireicon' /> {item.burnedCal}</div>
                                                </div>
                                            </div>
                                            <div className="session_content_col">
                                                <div className="session_content_label fw-medium mb-2">Accuracy</div>
                                                <div className="session_content_text text-white fw-bold">{item.accuracy.toFixed(2)}%</div>
                                            </div>
                                            <div className="session_content_col">
                                                <div className="session_content_label fw-medium mb-2">Time</div>
                                                <div className="session_content_text text-white fw-bold">{Math.round((item.time) / 60)} <span className="text-light">mins</span></div>
                                            </div>
                                        </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {sessionHistory && sessionHistory.length === 0 && (
                                <div className="text-center">
                                    <span className="text-white fw-bold">No games played in this session</span>
                                </div>
                            )}
                            { showDailyTarget && (
                                <div className="text-center">
                                    <button className="cta_button bg-warning" onClick={() => closeSessionHistoryModal()}>Continue</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {(showSessionHistoryModal) && <div className="modal-backdrop show"></div>}
        </>
    );
}

export default SessionHistory;
